<template>
  <div v-if="totalItems" class="rpage">
    <div class="rpage__info">
      Showing <strong>{{ startIndex + 1 }}–{{ endIndex }}</strong> of <strong>{{ totalItems }}</strong>

      <label v-if="showPerPage" class="rpage__per-page">
        <span>Rows per page</span>
        <div class="rpage__select-wrap">
          <select v-model.number="perPage" class="rpage__select">
            <option v-for="opt in perPageOptions" :key="opt" :value="opt">{{ opt }}</option>
          </select>
          <svg class="rpage__chevron" viewBox="0 0 20 20" fill="none">
            <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
      </label>
    </div>

    <div class="rpage__controls">
      <button
        type="button"
        class="rpage__btn rpage__btn--nav"
        :disabled="currentPage === 1"
        aria-label="Previous page"
        @click="goToPage(currentPage - 1)"
      >
        <svg viewBox="0 0 20 20" fill="none">
          <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>

      <template v-for="(p, i) in pageNumbers" :key="i">
        <span v-if="p === '...'" class="rpage__ellipsis">…</span>
        <button
          v-else
          type="button"
          class="rpage__btn"
          :class="{ 'rpage__btn--active': p === currentPage }"
          @click="goToPage(p)"
        >
          {{ p }}
        </button>
      </template>

      <button
        type="button"
        class="rpage__btn rpage__btn--nav"
        :disabled="currentPage === totalPages"
        aria-label="Next page"
        @click="goToPage(currentPage + 1)"
      >
        <svg viewBox="0 0 20 20" fill="none">
          <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'

const props = defineProps({
  // CLIENT MODE: pass the full array, this component slices it locally.
  data: { type: Array, default: () => [] },

  // SERVER MODE: pass your API's pagination object straight through, e.g.
  // :meta="response.pagination" where pagination looks like
  // { total_rows, per_page, current_page, total_pages }.
  // When `meta` is set, `data` is treated as already being just the current
  // page's rows — nothing is sliced, and the component instead emits
  // `page-change` so you can refetch from the API.
  meta: { type: Object, default: null },

  // Optional v-model for the current page (client mode only).
  modelValue: { type: Number, default: null },

  perPageDefault: { type: Number, default: 10 },
  showPerPage: { type: Boolean, default: true },
  perPageOptions: { type: Array, default: () => [10, 25, 50, 100] },

  // How many numbered buttons to show around the current page.
  maxButtons: { type: Number, default: 5 },
})

const emit = defineEmits(['update:modelValue', 'page-data', 'page-change'])

const isServer = computed(() => !!props.meta)

// Client-mode local state (unused in server mode)
const clientPage = ref(props.modelValue ?? 1)
const clientPerPage = ref(props.perPageDefault)

const currentPage = computed({
  get: () => (isServer.value ? props.meta.current_page : clientPage.value),
  set: (p) => {
    if (isServer.value) {
      // Nothing to slice locally — ask the caller to fetch this page.
      emit('page-change', { page: p, perPage: perPage.value })
    } else {
      clientPage.value = p
      emit('page-change', { page: p, perPage: perPage.value })
    }
  },
})

const perPage = computed({
  get: () => (isServer.value ? props.meta.per_page : clientPerPage.value),
  set: (val) => {
    const newPerPage = Number(val)
    if (isServer.value) {
      emit('page-change', { page: 1, perPage: newPerPage })
    } else {
      clientPerPage.value = newPerPage
      clientPage.value = 1
      emit('page-change', { page: 1, perPage: newPerPage })
    }
  },
})

const totalItems = computed(() => (isServer.value ? props.meta.total_rows : props.data.length))
const totalPages = computed(() =>
  isServer.value ? Math.max(1, props.meta.total_pages) : Math.max(1, Math.ceil(totalItems.value / perPage.value)),
)

const startIndex = computed(() => (totalItems.value ? (currentPage.value - 1) * perPage.value : 0))
// Server mode: `data` IS the current page, so the range end is just how many rows came back.
const endIndex = computed(() =>
  isServer.value ? startIndex.value + props.data.length : Math.min(startIndex.value + perPage.value, totalItems.value),
)
const pageData = computed(() => (isServer.value ? props.data : props.data.slice(startIndex.value, endIndex.value)))

const pageNumbers = computed(() => {
  const total = totalPages.value
  const current = currentPage.value
  const max = props.maxButtons

  if (total <= max) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  const half = Math.floor(max / 2)
  let start = Math.max(1, current - half)
  let end = start + max - 1
  if (end > total) {
    end = total
    start = Math.max(1, end - max + 1)
  }

  const pages = []
  if (start > 1) {
    pages.push(1)
    if (start > 2) pages.push('...')
  }
  for (let i = start; i <= end; i++) pages.push(i)
  if (end < total) {
    if (end < total - 1) pages.push('...')
    pages.push(total)
  }
  return pages
})

function goToPage(p) {
  if (p < 1 || p > totalPages.value || p === currentPage.value) return
  currentPage.value = p
}

// Client mode only: keep page in range if the dataset shrinks (e.g. a filter
// reduced the results). Server mode trusts the API's own current_page/total_pages.
watch(totalPages, (max) => {
  if (!isServer.value && clientPage.value > max) clientPage.value = max
})

// External control via v-model, client mode only.
watch(
  () => props.modelValue,
  (val) => {
    if (!isServer.value && val != null && val !== clientPage.value) clientPage.value = val
  },
)

watch(currentPage, (val) => emit('update:modelValue', val))

// Client mode only: emit the sliced page. In server mode `data` already IS
// the page, so there's nothing meaningful to re-emit here.
watch(
  pageData,
  (val) => {
    if (!isServer.value) emit('page-data', val)
  },
  { immediate: true },
)

defineExpose({ goToPage, currentPage, totalPages })
</script>

<style scoped>
.rpage {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 20px;
  margin-top: 10px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.65);
  box-shadow: 0 6px 20px -12px rgba(93, 28, 106, 0.18);
  font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 13px;
  color: var(--color-text-muted);
}
.rpage * {
  box-sizing: border-box;
}

.rpage__info {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
.rpage__info strong {
  color: var(--color-text-dark);
  font-weight: 700;
}

.rpage__per-page {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: var(--color-text-muted);
}
.rpage__select-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.rpage__select {
  appearance: none;
  height: 32px;
  padding: 0 26px 0 10px;
  border-radius: 9px;
  border: 1px solid var(--color-border);
  background: rgba(255, 255, 255, 0.7);
  font-size: 12.5px;
  font-weight: 600;
  color: var(--color-text-dark);
  cursor: pointer;
  outline: none;
}
.rpage__select:focus {
  border-color: var(--color-secondary);
  box-shadow: 0 0 0 3px rgba(202, 89, 149, 0.16);
}
.rpage__chevron {
  position: absolute;
  right: 8px;
  width: 11px;
  height: 11px;
  color: var(--color-text-muted);
  pointer-events: none;
}

.rpage__controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.rpage__btn {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border-radius: 9px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--color-text-dark);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease, transform 0.12s ease;
}
.rpage__btn:hover:not(:disabled):not(.rpage__btn--active) {
  background: var(--color-tint-light);
}
.rpage__btn:active:not(:disabled) {
  transform: scale(0.95);
}
.rpage__btn--active {
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  box-shadow: 0 4px 12px -4px rgba(93, 28, 106, 0.45);
}
.rpage__btn--nav svg {
  width: 15px;
  height: 15px;
}
.rpage__btn:disabled {
  color: var(--color-text-muted);
  opacity: 0.4;
  cursor: not-allowed;
}

.rpage__ellipsis {
  min-width: 24px;
  text-align: center;
  color: var(--color-text-muted);
}

@media (max-width: 640px) {
  .rpage {
    justify-content: center;
    text-align: center;
  }
  .rpage__info {
    justify-content: center;
    width: 100%;
  }
  .rpage__controls {
    width: 100%;
    justify-content: center;
  }
}
</style>